/**
 * PER-EMPLOYEE PAY EXCEPTIONS — special pay rules that OVERRIDE the normal payroll
 * rules for one named person.
 *
 * WHY THIS EXISTS: every pay decision WorkLogix makes is derived — the earning type
 * from (entry kind + department) intersected with the employee's own NetSuite
 * earnings, overtime from a flat 40-hour week, the rate from the mirrored earning
 * rate. Those derivations are correct for the workforce but wrong for a handful of
 * individuals whose arrangement is genuinely different. Without a first-class place
 * to record that, the only ways to express it are to distort the shared rule (which
 * changes everyone's pay) or to fix it by hand every week (which is silent and gets
 * forgotten). Both are worse than a stored, visible, audited override.
 *
 * TWO RULES DECIDED BY MICHAEL (2026-09-02) SHAPE EVERYTHING HERE:
 *
 *   1. STANDING UNTIL REMOVED. An exception has no start or end date. It applies
 *      from the moment it is saved until someone deactivates or deletes it. There is
 *      deliberately no effective-dating: a date range nobody maintains is a rule
 *      that silently expires mid-payroll, and "why did this person's pay change?"
 *      is then invisible. Deactivating is an explicit, attributable act.
 *
 *   2. FIELD-LEVEL OVERRIDE, NOT WHOLESALE REPLACEMENT. An exception names exactly
 *      ONE field and overrides only that field; every other pay decision for that
 *      employee still comes from the normal rules. So "never gets overtime" changes
 *      the overtime decision and nothing else — their earning type and rate keep
 *      following the shared map. An employee needing two fields changed gets two
 *      exceptions. This is why {@link PayExceptionRule} is a discriminated union on
 *      `field` rather than a bag of optional overrides: a partially-filled bag
 *      cannot express "override this and only this" without ambiguity about whether
 *      an absent value means "no change" or "set to empty".
 *
 * PURE and dependency-free (like {@link import('./overtime')}), so the API, the
 * TimeBill poster and the browser share ONE implementation of precedence. A second
 * copy of a payroll rule is a second answer to the same question.
 */
import { EARNING_MAP } from './earning-map.generated.js';
import { resolveEarning, resolveEarningFlag, } from './earnings.js';
import { WEEKLY_REGULAR_HOURS_LIMIT } from './overtime.js';
import { allocateAmount } from './pay-allocation.js';
/** Every field an exception may name, for validation and UI enumeration. */
export const PAY_EXCEPTION_FIELDS = Object.freeze([
    'earning',
    'overtime',
    'rate',
    'commission_split',
]);
/** Type guard for a raw string off the wire or out of the database. */
export function isPayExceptionField(value) {
    return PAY_EXCEPTION_FIELDS.includes(value);
}
/**
 * Assemble the discriminated rule from the flat wire shape, dropping the value
 * columns that do not belong to the named field.
 *
 * DROPPING rather than passing them through is the point: a form that has been
 * switched from `rate` to `overtime` still holds the old rate in its state, and
 * carrying it into the payload would store a row the table's CHECK constraints
 * reject — or worse, one that a laxer path accepts. Returns null when the named
 * field's own value is missing, which {@link validatePayException} then reports.
 */
export function payExceptionInputFromWire(wire) {
    const base = {
        employeeId: wire.employeeId,
        entryKind: wire.entryKind,
        nsDepartmentId: wire.nsDepartmentId,
        note: wire.note,
        active: wire.active,
    };
    switch (wire.field) {
        case 'earning':
            return wire.nsPayrollItemId === null
                ? null
                : { ...base, rule: { field: 'earning', nsPayrollItemId: wire.nsPayrollItemId } };
        case 'overtime':
            return wire.overtimeExempt === null
                ? null
                : {
                    ...base,
                    rule: {
                        field: 'overtime',
                        exempt: wire.overtimeExempt,
                        // An exempt employee has no limit; storing one would be a value no
                        // code path reads, waiting to mislead whoever reads the row next.
                        weeklyRegularHoursLimit: wire.overtimeExempt ? null : wire.weeklyRegularHoursLimit,
                    },
                };
        case 'rate':
            return wire.hourlyRate === null
                ? null
                : { ...base, rule: { field: 'rate', hourlyRate: wire.hourlyRate } };
        case 'commission_split':
            return wire.transferToEmployeeId === null || wire.transferPercent === null
                ? null
                : {
                    ...base,
                    rule: {
                        field: 'commission_split',
                        toEmployeeId: wire.transferToEmployeeId,
                        percent: wire.transferPercent,
                    },
                };
        /* c8 ignore next 2 */
        default:
            return null;
    }
}
/**
 * How specifically an exception's scope matches a given (kind, department).
 *
 * Higher wins. The weights make every combination DISTINCT — 3 (both) > 2 (kind
 * only) > 1 (department only) > 0 (employee-wide) — so "most specific wins" is
 * total and no tie-break is ever needed. Kind outranks department deliberately:
 * an exception naming a kind ("their PTO is different") is a statement about the
 * nature of the pay, while a department is a statement about where it was worked,
 * and the former is the narrower claim.
 *
 * Returns null when the exception does not match at all.
 */
function scopeScore(ex, kind, nsDepartmentId) {
    let score = 0;
    if (ex.entryKind !== null) {
        if (ex.entryKind !== kind) {
            return null;
        }
        score += 2;
    }
    if (ex.nsDepartmentId !== null) {
        if (ex.nsDepartmentId !== nsDepartmentId) {
            return null;
        }
        score += 1;
    }
    return score;
}
/**
 * The exception that governs one field for one (kind, department), or undefined
 * when the normal rule stands.
 *
 * Only ACTIVE exceptions are considered. Pass `kind`/`nsDepartmentId` as null to
 * ask the employee-wide question (as `overtime` does) — a null argument matches
 * only an exception that is itself unscoped on that axis, so a department-scoped
 * rule can never leak into a decision made across departments.
 */
export function selectPayException(exceptions, field, kind, nsDepartmentId) {
    let best;
    let bestScore = -1;
    for (const ex of exceptions) {
        if (!ex.active || ex.rule.field !== field) {
            continue;
        }
        const score = scopeScore(ex, kind, nsDepartmentId);
        if (score !== null && score > bestScore) {
            best = ex;
            bestScore = score;
        }
    }
    return best;
}
/** Display name for a NetSuite payroll item id, falling back to the raw id. */
function earningName(nsPayrollItemId) {
    const match = EARNING_MAP.find((m) => m.nsPayrollItemId === nsPayrollItemId);
    return match !== undefined ? match.name : `NS #${nsPayrollItemId}`;
}
/**
 * {@link resolveEarning}, with a per-employee `earning` exception taking
 * precedence over the (kind + department) map.
 *
 * A forced earning the employee does NOT hold resolves to `missing` naming that
 * one earning, not to the forced id: NetSuite rejects an earning absent from the
 * employee's file, so resolving it would trade a visible flag for a failed post.
 * The flag says exactly what payroll must add.
 *
 * With no matching exception this is `resolveEarning` unchanged — which is why an
 * empty exception table leaves every existing pay decision byte-for-byte identical.
 */
export function resolveEarningWithExceptions(kind, nsDepartmentId, employeeEarningIds, exceptions) {
    const ex = selectPayException(exceptions, 'earning', kind, nsDepartmentId);
    if (ex === undefined || ex.rule.field !== 'earning') {
        return resolveEarning(kind, nsDepartmentId, employeeEarningIds);
    }
    const forced = ex.rule.nsPayrollItemId;
    const name = earningName(forced);
    return employeeEarningIds.has(forced)
        ? { status: 'resolved', nsPayrollItemId: forced, candidates: [name] }
        : { status: 'missing', candidates: [name] };
}
/**
 * {@link resolveEarningFlag}, honouring an `earning` exception.
 *
 * The flag surface MUST agree with the posting surface. If an exception makes an
 * entry postable, a grid still showing "no earning type on this employee's file"
 * is telling payroll to fix something that is already handled — and if the
 * exception forces an earning the employee does not hold, the flag has to appear,
 * because NetSuite will reject that save outright.
 */
export function resolveEarningFlagWithExceptions(kind, nsDepartmentId, departmentName, heldEarningIds, exceptions) {
    const ex = selectPayException(exceptions, 'earning', kind, nsDepartmentId);
    if (ex === undefined || ex.rule.field !== 'earning') {
        return resolveEarningFlag(kind, nsDepartmentId, departmentName, heldEarningIds);
    }
    // A forced earning does not need a NetSuite department to resolve — the exception
    // already names the item — so an unmapped department is no longer a blocker here.
    const forced = ex.rule.nsPayrollItemId;
    if (heldEarningIds.has(forced)) {
        return undefined;
    }
    const name = earningName(forced);
    return {
        kind,
        status: 'missing',
        departmentName,
        reason: `a pay exception posts this ${kind} under ${name}, but that earning is not on ` +
            `this employee's file — add it, or remove the exception`,
        candidates: [name],
    };
}
/**
 * Is this employee exempt from overtime, honouring an `overtime` exception?
 *
 * The exception is authoritative in BOTH directions. It can exempt someone the
 * earnings-derived rule would pay overtime to, and it can also un-exempt: an
 * exception with `exempt: false` restores overtime to an employee who holds a VISA
 * earning. That matters because the derived rule reads what they HOLD in NetSuite,
 * and an individual arrangement has to be able to disagree with it — otherwise the
 * only way to express the exception is to edit their NetSuite earnings, which
 * changes what they can be paid under, not just whether overtime applies.
 */
export function isOvertimeExemptWithExceptions(derivedExempt, exceptions) {
    const ex = selectPayException(exceptions, 'overtime', null, null);
    if (ex === undefined || ex.rule.field !== 'overtime') {
        return derivedExempt;
    }
    return ex.rule.exempt;
}
/**
 * The weekly regular-hours limit for this employee — the standard 40 unless an
 * `overtime` exception names a different one.
 *
 * An exempt employee has no meaningful limit (nothing becomes overtime), so the
 * standard value is returned rather than a special number the caller would have to
 * interpret; callers gate on {@link isOvertimeExemptWithExceptions} first.
 */
export function weeklyRegularHoursLimitWithExceptions(exceptions) {
    const ex = selectPayException(exceptions, 'overtime', null, null);
    if (ex === undefined || ex.rule.field !== 'overtime' || ex.rule.exempt) {
        return WEEKLY_REGULAR_HOURS_LIMIT;
    }
    return ex.rule.weeklyRegularHoursLimit ?? WEEKLY_REGULAR_HOURS_LIMIT;
}
/**
 * The hourly rate to pay at: a `rate` exception if one governs this
 * (kind, department), else the rate mirrored from NetSuite.
 *
 * `null` in and `null` out means UNKNOWN, never zero — the same convention as
 * `employee_earning.rate`. An exception replaces an unknown rate with a known one,
 * which is one of its main uses.
 */
export function hourlyRateWithExceptions(derivedRate, exceptions, kind, nsDepartmentId) {
    const ex = selectPayException(exceptions, 'rate', kind, nsDepartmentId);
    if (ex === undefined || ex.rule.field !== 'rate') {
        return derivedRate;
    }
    return ex.rule.hourlyRate;
}
/**
 * The commission-split rule governing this employee's commissions, or undefined.
 *
 * Employee-wide by definition: a commission is not clocked at a department, so the
 * scope axes do not apply and a scoped rule is refused at write time.
 */
export function selectCommissionSplit(exceptions) {
    return selectPayException(exceptions, 'commission_split', null, null);
}
/**
 * Split a commission between the earner and the recipient named by their rule.
 *
 * Returns a SINGLE share for the earner when no rule applies, so a caller can treat
 * "split" and "not split" identically instead of branching — the un-split case is
 * just a split of one.
 *
 * The two shares are apportioned with {@link allocateAmount}, in cents, so they add
 * back to the commission exactly. That matters more here than in a department split:
 * these are two separate people's pay, and a lost cent is one of them being underpaid
 * while the books still balance against the original figure.
 *
 * The RECIPIENT is ordered last and carries the leftover cent, matching how the
 * department splits place theirs — and the retained share is the larger of the two in
 * Michael's case (65 vs 35), so the earner is never the one shorted by rounding.
 */
export function splitCommission(amount, earnerEmployeeId, exceptions) {
    const ex = selectCommissionSplit(exceptions);
    if (ex === undefined || ex.rule.field !== 'commission_split') {
        return [{ employeeId: earnerEmployeeId, amount, retained: true }];
    }
    const away = ex.rule.percent;
    const parts = allocateAmount(amount, [
        { departmentId: earnerEmployeeId, nsDepartmentId: null, percent: 100 - away },
        { departmentId: ex.rule.toEmployeeId, nsDepartmentId: null, percent: away },
    ]);
    return parts.map((p, i) => ({
        employeeId: p.departmentId,
        amount: p.amount,
        retained: i === 0,
    }));
}
/** Group exceptions by employee id — one pass, then O(1) per employee. */
export function groupExceptionsByEmployee(exceptions) {
    const byEmployee = new Map();
    for (const ex of exceptions) {
        const bucket = byEmployee.get(ex.employeeId);
        if (bucket === undefined) {
            byEmployee.set(ex.employeeId, [ex]);
        }
        else {
            bucket.push(ex);
        }
    }
    return byEmployee;
}
/** One-line description of what an exception does, for grids and flag messages. */
export function payExceptionSummary(ex) {
    const scope = [];
    if (ex.entryKind !== null) {
        scope.push(ex.entryKind);
    }
    if (ex.nsDepartmentId !== null) {
        scope.push(`dept ${ex.nsDepartmentId}`);
    }
    const suffix = scope.length === 0 ? '' : ` (${scope.join(', ')})`;
    switch (ex.rule.field) {
        case 'earning':
            return `Earning: ${earningName(ex.rule.nsPayrollItemId)}${suffix}`;
        case 'overtime':
            return ex.rule.exempt
                ? 'Overtime: exempt — no overtime at any hours'
                : `Overtime: above ${ex.rule.weeklyRegularHoursLimit ?? WEEKLY_REGULAR_HOURS_LIMIT} h/week`;
        case 'rate':
            return `Rate: ${ex.rule.hourlyRate.toFixed(2)}/h${suffix}`;
        case 'commission_split':
            return `Commission: ${ex.rule.percent.toFixed(2)}% to another employee, ${(100 - ex.rule.percent).toFixed(2)}% retained`;
        /* c8 ignore next 2 */
        default:
            return '';
    }
}
/**
 * Validate an exception before it is stored, returning a human reason or null.
 *
 * Enforced HERE rather than only in a DTO so the API and the browser reject the
 * same things for the same reasons, and so the invariants that a CHECK constraint
 * cannot express (a field's rule shape, the employee-wide scope of `overtime`)
 * still fail closed.
 */
export function validatePayException(input) {
    if (input.employeeId.trim() === '') {
        return 'An exception must name an employee.';
    }
    if (input.note.trim() === '') {
        return 'Say why this exception exists — an unexplained pay override reads as a mistake later.';
    }
    switch (input.rule.field) {
        case 'earning':
            if (input.rule.nsPayrollItemId.trim() === '') {
                return 'Choose the earning type to post under.';
            }
            return null;
        case 'overtime':
            if (input.entryKind !== null || input.nsDepartmentId !== null) {
                return 'An overtime exception applies to the whole employee — it cannot be scoped to one kind or department, because a week can span both.';
            }
            if (!input.rule.exempt) {
                const limit = input.rule.weeklyRegularHoursLimit;
                if (limit === null) {
                    return 'Either exempt the employee from overtime or give the weekly regular-hours limit.';
                }
                if (!Number.isFinite(limit) || limit <= 0 || limit > 168) {
                    return 'The weekly regular-hours limit must be between 0 and 168 hours.';
                }
            }
            return null;
        case 'rate':
            if (!Number.isFinite(input.rule.hourlyRate) || input.rule.hourlyRate <= 0) {
                return 'The hourly rate must be greater than zero.';
            }
            return null;
        case 'commission_split':
            if (input.entryKind !== null || input.nsDepartmentId !== null) {
                return 'A commission split applies to the whole employee — a commission is not clocked at a department, so it cannot be scoped.';
            }
            if (input.rule.toEmployeeId.trim() === '') {
                return 'Choose the employee who receives the share.';
            }
            if (input.rule.toEmployeeId === input.employeeId) {
                return 'A commission cannot be split to the same employee it came from.';
            }
            if (!Number.isFinite(input.rule.percent) ||
                input.rule.percent <= 0 ||
                input.rule.percent >= 100) {
                return 'The transferred share must be greater than 0% and less than 100%.';
            }
            return null;
        /* c8 ignore next 2 */
        default:
            return 'Unknown exception field.';
    }
}
//# sourceMappingURL=pay-exception.js.map